import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiSave, FiArrowLeft, FiPlus, FiTrash2 } from 'react-icons/fi';
import api from '../../services/api';
import TradePartnerDocsTab from './TradePartnerDocsTab';

const TP_TYPES = ['Customer', 'Vendor', 'Trucker', 'Warehouse', 'Terminal', 'Others'];

const TradePartnerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [partnerId, setPartnerId] = useState(isNew ? null : id);

  const [form, setForm] = useState({
    tp_type: 'Customer', name: '', alias: '', local_name: '', print_name: '', ceo: '',
    address: '', city: '', state: '', zip_code: '', country: '',
    tel: '', fax: '', email: '', url: '', firms_code: '',
    contact_person: '', contact_phone: '', contact_email: '', remark: '',
    accounting_address: '', tax_id: '', payment_type: 'COD', credit_term: '', credit_term_days: 0, credit_limit: 0,
    accountant_name: '', bank_name: '', account_no: '', currency: 'USD',
    sales: '', op: '', sales_office: '', status: 'Active',
  });

  const [defaultCharges, setDefaultCharges] = useState({
    AR: [],
    AP: [],
    DC: [],
  });

  useEffect(() => {
    if (!isNew) {
      loadPartner();
    }
  }, [id]);

  const loadPartner = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/trade-partners/${id}`);
      const { defaultCharges: charges, ...partnerData } = res.data;
      setForm(partnerData);
      setPartnerId(id);

      // Group charges by section
      const grouped = { AR: [], AP: [], DC: [] };
      (charges || []).forEach(c => {
        if (grouped[c.charge_section]) {
          grouped[c.charge_section].push(c);
        }
      });
      setDefaultCharges(grouped);
    } catch (error) {
      toast.error('Failed to load trade partner');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.name?.trim()) {
      toast.error('Name is required');
      return;
    }
    try {
      setSaving(true);

      if (isNew && !partnerId) {
        // Create new trade partner on first save
        const res = await api.post('/trade-partners', form);
        const newId = res.data.id;
        setPartnerId(newId);
        setForm(prev => ({ ...prev, code: res.data.code }));

        // Save default charges if any
        const allCharges = [
          ...defaultCharges.AR.map((c, i) => ({ ...c, charge_section: 'AR', sort_order: i })),
          ...defaultCharges.AP.map((c, i) => ({ ...c, charge_section: 'AP', sort_order: i })),
          ...defaultCharges.DC.map((c, i) => ({ ...c, charge_section: 'DC', sort_order: i })),
        ];
        if (allCharges.length > 0) {
          await api.post(`/trade-partners/${newId}/default-charges/bulk`, { charges: allCharges });
        }

        toast.success(`Trade partner ${res.data.code} created`);
        navigate(`/trade-partner/${newId}`, { replace: true });
      } else {
        // Update existing
        await api.put(`/trade-partners/${partnerId}`, form);

        // Save default charges (bulk)
        const allCharges = [
          ...defaultCharges.AR.map((c, i) => ({ ...c, charge_section: 'AR', sort_order: i })),
          ...defaultCharges.AP.map((c, i) => ({ ...c, charge_section: 'AP', sort_order: i })),
          ...defaultCharges.DC.map((c, i) => ({ ...c, charge_section: 'DC', sort_order: i })),
        ];
        await api.post(`/trade-partners/${partnerId}/default-charges/bulk`, { charges: allCharges });

        toast.success('Trade partner saved');
      }
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const copyLocalAddress = () => {
    handleChange('accounting_address', `${form.address || ''}\n${form.city || ''}, ${form.state || ''} ${form.zip_code || ''}\n${form.country || ''}`);
  };

  if (loading) {
    return <div className="loading"><div className="loading-spinner"></div></div>;
  }

  const tabs = [
    { key: 'basic', label: 'Basic' },
    { key: 'accounting', label: 'Accounting Setting' },
    { key: 'documents', label: 'Doc Center' },
  ];

  return (
    <div>
      {/* Header */}
      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button className="btn btn-secondary" onClick={() => navigate('/trade-partner')}><FiArrowLeft /></button>
            <h2 className="card-title" style={{ margin: 0 }}>
              {form.code ? `Trade Partner ${form.code}` : 'New Trade Partner'}
            </h2>
            {form.tp_type && (
              <span style={{
                padding: '2px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
                background: '#dbeafe', color: '#2563eb',
              }}>{form.tp_type}</span>
            )}
          </div>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            <FiSave /> {saving ? 'Saving...' : 'Save'}
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '2px solid #e2e8f0', padding: '0 20px' }}>
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: '10px 20px', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '13px',
                borderBottom: activeTab === tab.key ? '3px solid #2563eb' : '3px solid transparent',
                color: activeTab === tab.key ? '#2563eb' : '#64748b',
                background: 'transparent', marginBottom: '-2px',
              }}
            >{tab.label}</button>
          ))}
        </div>
      </div>

      {activeTab === 'basic' && (
        <BasicTab form={form} handleChange={handleChange} copyLocalAddress={copyLocalAddress} />
      )}
      {activeTab === 'accounting' && (
        <AccountingSettingTab defaultCharges={defaultCharges} setDefaultCharges={setDefaultCharges} />
      )}
      {activeTab === 'documents' && (
        <TradePartnerDocsTab partnerId={partnerId} />
      )}
    </div>
  );
};

// =================== BASIC TAB ===================
const BasicTab = ({ form, handleChange, copyLocalAddress }) => (
  <div className="card" style={{ marginTop: 0, borderTop: 'none' }}>
    {/* Trade Partner Information */}
    <div className="form-section-header">Trade Partner Information</div>
    <div className="shipment-form" style={{ padding: '20px' }}>
      <div className="shipment-form-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className="shipment-form-group">
          <label>* TP Type</label>
          <select value={form.tp_type} onChange={(e) => handleChange('tp_type', e.target.value)}>
            {TP_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="shipment-form-group">
          <label>Code</label>
          <input type="text" value={form.code || ''} readOnly style={{ background: '#f1f5f9' }} />
        </div>
        <div className="shipment-form-group">
          <label>* Name</label>
          <input type="text" value={form.name || ''} onChange={(e) => handleChange('name', e.target.value)} />
        </div>
        <div className="shipment-form-group">
          <label>Alias</label>
          <input type="text" value={form.alias || ''} onChange={(e) => handleChange('alias', e.target.value)} />
        </div>
        <div className="shipment-form-group">
          <label>Local Name</label>
          <input type="text" value={form.local_name || ''} onChange={(e) => handleChange('local_name', e.target.value)} />
        </div>
        <div className="shipment-form-group">
          <label>Print Name</label>
          <input type="text" value={form.print_name || ''} onChange={(e) => handleChange('print_name', e.target.value)} />
        </div>
        <div className="shipment-form-group">
          <label>CEO</label>
          <input type="text" value={form.ceo || ''} onChange={(e) => handleChange('ceo', e.target.value)} />
        </div>
        <div className="shipment-form-group">
          <label>Status</label>
          <select value={form.status || 'Active'} onChange={(e) => handleChange('status', e.target.value)}>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Address */}
      <div style={{ marginTop: '16px' }}>
        <label style={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>Local Address</label>
        <textarea rows={2} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', resize: 'vertical' }}
          value={form.address || ''} onChange={(e) => handleChange('address', e.target.value)} />
      </div>

      <div className="shipment-form-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginTop: '8px' }}>
        <div className="shipment-form-group">
          <label>City</label>
          <input type="text" value={form.city || ''} onChange={(e) => handleChange('city', e.target.value)} />
        </div>
        <div className="shipment-form-group">
          <label>State</label>
          <input type="text" value={form.state || ''} onChange={(e) => handleChange('state', e.target.value)} />
        </div>
        <div className="shipment-form-group">
          <label>Zip Code</label>
          <input type="text" value={form.zip_code || ''} onChange={(e) => handleChange('zip_code', e.target.value)} />
        </div>
        <div className="shipment-form-group">
          <label>Country</label>
          <input type="text" value={form.country || ''} onChange={(e) => handleChange('country', e.target.value)} />
        </div>
        <div className="shipment-form-group">
          <label>TEL</label>
          <input type="text" value={form.tel || ''} onChange={(e) => handleChange('tel', e.target.value)} />
        </div>
        <div className="shipment-form-group">
          <label>FAX</label>
          <input type="text" value={form.fax || ''} onChange={(e) => handleChange('fax', e.target.value)} />
        </div>
        <div className="shipment-form-group">
          <label>Email</label>
          <input type="email" value={form.email || ''} onChange={(e) => handleChange('email', e.target.value)} />
        </div>
        <div className="shipment-form-group">
          <label>URL</label>
          <input type="text" value={form.url || ''} onChange={(e) => handleChange('url', e.target.value)} />
        </div>
        <div className="shipment-form-group">
          <label>Firms Code</label>
          <input type="text" value={form.firms_code || ''} onChange={(e) => handleChange('firms_code', e.target.value)} />
        </div>
      </div>

      {/* Contact */}
      <div className="shipment-form-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginTop: '8px' }}>
        <div className="shipment-form-group">
          <label>Contact Person</label>
          <input type="text" value={form.contact_person || ''} onChange={(e) => handleChange('contact_person', e.target.value)} />
        </div>
        <div className="shipment-form-group">
          <label>Contact Phone</label>
          <input type="text" value={form.contact_phone || ''} onChange={(e) => handleChange('contact_phone', e.target.value)} />
        </div>
        <div className="shipment-form-group">
          <label>Contact Email</label>
          <input type="email" value={form.contact_email || ''} onChange={(e) => handleChange('contact_email', e.target.value)} />
        </div>
        <div className="shipment-form-group">
          <label>Sales</label>
          <input type="text" value={form.sales || ''} onChange={(e) => handleChange('sales', e.target.value)} />
        </div>
        <div className="shipment-form-group">
          <label>OP</label>
          <input type="text" value={form.op || ''} onChange={(e) => handleChange('op', e.target.value)} />
        </div>
        <div className="shipment-form-group">
          <label>Sales Office</label>
          <input type="text" value={form.sales_office || ''} onChange={(e) => handleChange('sales_office', e.target.value)} />
        </div>
      </div>

      {/* Remark */}
      <div style={{ marginTop: '16px' }}>
        <label style={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>Remark</label>
        <textarea rows={3} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', resize: 'vertical' }}
          value={form.remark || ''} onChange={(e) => handleChange('remark', e.target.value)} />
      </div>
    </div>

    {/* Accounting Setting within Basic */}
    <div className="form-section-header">Accounting Setting</div>
    <div className="shipment-form" style={{ padding: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <label style={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>Accounting Address</label>
        <button className="btn btn-sm btn-secondary" onClick={copyLocalAddress} style={{ fontSize: '11px' }}>
          Copy from Local Address
        </button>
      </div>
      <textarea rows={2} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', resize: 'vertical', marginBottom: '12px' }}
        value={form.accounting_address || ''} onChange={(e) => handleChange('accounting_address', e.target.value)} />

      <div className="shipment-form-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className="shipment-form-group">
          <label>Tax ID / USCI No.</label>
          <input type="text" value={form.tax_id || ''} onChange={(e) => handleChange('tax_id', e.target.value)} />
        </div>
        <div className="shipment-form-group">
          <label>Payment Type</label>
          <select value={form.payment_type || 'COD'} onChange={(e) => handleChange('payment_type', e.target.value)}>
            <option value="COD">COD</option>
            <option value="Prepaid">Prepaid</option>
            <option value="Collect">Collect</option>
            <option value="Credit">Credit</option>
          </select>
        </div>
        <div className="shipment-form-group">
          <label>Credit Term</label>
          <select value={form.credit_term || ''} onChange={(e) => handleChange('credit_term', e.target.value)}>
            <option value="">Select...</option>
            <option value="Days_15">Days 15</option>
            <option value="Days_30">Days 30</option>
            <option value="Days_45">Days 45</option>
            <option value="Days_60">Days 60</option>
            <option value="Days_90">Days 90</option>
          </select>
        </div>
        <div className="shipment-form-group">
          <label>Credit Limit</label>
          <input type="number" value={form.credit_limit || 0} onChange={(e) => handleChange('credit_limit', parseFloat(e.target.value) || 0)} />
        </div>
        <div className="shipment-form-group">
          <label>Accountant Name</label>
          <input type="text" value={form.accountant_name || ''} onChange={(e) => handleChange('accountant_name', e.target.value)} />
        </div>
        <div className="shipment-form-group">
          <label>Bank Name</label>
          <input type="text" value={form.bank_name || ''} onChange={(e) => handleChange('bank_name', e.target.value)} />
        </div>
        <div className="shipment-form-group">
          <label>Account No.</label>
          <input type="text" value={form.account_no || ''} onChange={(e) => handleChange('account_no', e.target.value)} />
        </div>
        <div className="shipment-form-group">
          <label>Currency</label>
          <select value={form.currency || 'USD'} onChange={(e) => handleChange('currency', e.target.value)}>
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
            <option value="GBP">GBP</option>
            <option value="CNY">CNY</option>
            <option value="JPY">JPY</option>
            <option value="CAD">CAD</option>
          </select>
        </div>
      </div>
    </div>
  </div>
);

// =================== ACCOUNTING SETTING TAB ===================
const AccountingSettingTab = ({ defaultCharges, setDefaultCharges }) => {
  const sections = [
    { key: 'AR', title: 'Invoice (A/R) Default' },
    { key: 'AP', title: 'AP Default' },
    { key: 'DC', title: 'D/C Note Default' },
  ];

  const addRow = (section) => {
    setDefaultCharges(prev => ({
      ...prev,
      [section]: [...prev[section], {
        freight_code: '', freight_description: '', type: section === 'AR' ? 'Sell' : 'Our Cost',
        unit: 'UNIT', volume: 1, rate: 0, amount: 0, agent_amount: 0,
      }],
    }));
  };

  const updateRow = (section, index, field, value) => {
    setDefaultCharges(prev => {
      const updated = [...prev[section]];
      updated[index] = { ...updated[index], [field]: value };
      // Auto-calc amount = volume * rate
      if (field === 'volume' || field === 'rate') {
        const vol = field === 'volume' ? (parseFloat(value) || 0) : (parseFloat(updated[index].volume) || 0);
        const rate = field === 'rate' ? (parseFloat(value) || 0) : (parseFloat(updated[index].rate) || 0);
        updated[index].amount = vol * rate;
      }
      return { ...prev, [section]: updated };
    });
  };

  const deleteRow = (section, index) => {
    setDefaultCharges(prev => ({
      ...prev,
      [section]: prev[section].filter((_, i) => i !== index),
    }));
  };

  return (
    <div className="card" style={{ marginTop: 0, borderTop: 'none' }}>
      <div className="form-section-header">Default Freight Setting</div>
      <div style={{ padding: '20px' }}>
        {sections.map(({ key, title }) => (
          <div key={key} style={{ marginBottom: '28px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#1e293b' }}>{title}</h4>
              <button className="btn btn-sm btn-primary" onClick={() => addRow(key)} style={{ padding: '4px 10px' }}>
                <FiPlus style={{ marginRight: '4px' }} /> Add
              </button>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table" style={{ minWidth: '900px' }}>
                <thead>
                  <tr>
                    <th style={{ width: '180px' }}>Freight Code</th>
                    <th style={{ width: '220px' }}>Freight Description</th>
                    <th style={{ width: '120px' }}>Type</th>
                    <th style={{ width: '100px' }}>Unit</th>
                    <th style={{ width: '80px', textAlign: 'right' }}>Vol.</th>
                    <th style={{ width: '100px', textAlign: 'right' }}>Rate</th>
                    <th style={{ width: '110px', textAlign: 'right' }}>Amount</th>
                    <th style={{ width: '110px', textAlign: 'right' }}>Agent Amount</th>
                    <th style={{ width: '50px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {defaultCharges[key].length === 0 ? (
                    <tr>
                      <td colSpan={9} style={{ textAlign: 'center', color: '#94a3b8', padding: '16px' }}>
                        No Data Available. Click <span style={{ color: '#2563eb', cursor: 'pointer' }} onClick={() => addRow(key)}>here</span> to add a new row.
                      </td>
                    </tr>
                  ) : defaultCharges[key].map((row, idx) => (
                    <tr key={idx}>
                      <td>
                        <input type="text" value={row.freight_code || ''} onChange={(e) => updateRow(key, idx, 'freight_code', e.target.value)}
                          style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '4px 6px', fontSize: '12px' }} />
                      </td>
                      <td>
                        <input type="text" value={row.freight_description || ''} onChange={(e) => updateRow(key, idx, 'freight_description', e.target.value)}
                          style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '4px 6px', fontSize: '12px' }} />
                      </td>
                      <td>
                        <select value={row.type || ''} onChange={(e) => updateRow(key, idx, 'type', e.target.value)}
                          style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '4px 6px', fontSize: '12px' }}>
                          <option value="Sell">Sell</option>
                          <option value="Our Cost">Our Cost</option>
                          <option value="Agent">Agent</option>
                        </select>
                      </td>
                      <td>
                        <select value={row.unit || 'UNIT'} onChange={(e) => updateRow(key, idx, 'unit', e.target.value)}
                          style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '4px 6px', fontSize: '12px' }}>
                          <option value="UNIT">UNIT</option>
                          <option value="BL">B/L</option>
                          <option value="CNTR">CNTR</option>
                          <option value="KG">KG</option>
                          <option value="CBM">CBM</option>
                          <option value="LOT">LOT</option>
                          <option value="SET">SET</option>
                        </select>
                      </td>
                      <td>
                        <input type="number" value={row.volume ?? 1} onChange={(e) => updateRow(key, idx, 'volume', e.target.value)}
                          style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '4px 6px', fontSize: '12px', textAlign: 'right' }} />
                      </td>
                      <td>
                        <input type="number" value={row.rate ?? 0} onChange={(e) => updateRow(key, idx, 'rate', e.target.value)}
                          style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '4px 6px', fontSize: '12px', textAlign: 'right' }} />
                      </td>
                      <td>
                        <input type="number" value={row.amount ?? 0} readOnly
                          style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '4px 6px', fontSize: '12px', textAlign: 'right', background: '#f8fafc' }} />
                      </td>
                      <td>
                        <input type="number" value={row.agent_amount ?? 0} onChange={(e) => updateRow(key, idx, 'agent_amount', e.target.value)}
                          style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '4px 6px', fontSize: '12px', textAlign: 'right' }} />
                      </td>
                      <td>
                        <button onClick={() => deleteRow(key, idx)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: '4px' }}>
                          <FiTrash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TradePartnerDetail;
